import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { v4 as uuidv4 } from 'uuid'
import { initializeWithCountIfNotInitialized } from '@mobi/account/Areas/Deposit/Utils'
import { Icon } from '@mobi/component-library/Legacy/Icon'
import { useBodyScrollLock } from '@mobi/utils/hooks/useBodyScrollLock'
import { incrementQuickCounter } from '@mobi/utils/logging/quickCounter'
import { navigateToChangeTempPassword } from '@core/Areas/ChangePassword/ChangeTemporaryPassword'
import { trackLoginSuccess } from '@core/Areas/Login/helpers/analytics'
import { state$ as navigationState$ } from '@core/State/Navigation/driver'
import { useAppSelector } from '@core/Store/hooks'
import { AccountNumberRecovery, LoginMain, PasswordReset } from './Components'
import { LoginVerificationCodePage } from './Components/MFALogin/LoginVerificationCodePage'
import { LoginContainerStyled } from './Login.styles'
import { setIsLoginShown } from './Store'
import { getIsLoginShown, getTrustedDeviceDetails } from './Store/selectors'
import { attemptLogin } from './helpers'
import { useBiometricsCredentials } from './helpers/useBiometricsCredentials'
import { useLoginState } from './hooks'

export const Login: React.FC = () => {
  const dispatch = useDispatch()
  const isLoginShown = useSelector(getIsLoginShown)

  React.useEffect(() => {
    const subscription = navigationState$
      .map(navState => navState.toJS().currentUrl)
      .skip(1)
      .distinctUntilChanged()
      .subscribe(() => dispatch(setIsLoginShown({ shouldShow: false })))

    return () => subscription?.dispose()
  }, [dispatch])

  return isLoginShown ? <LoginOverlay /> : null
}

const LoginOverlay = React.memo(() => {
  const loginContainerRef = useBodyScrollLock()
  const { state, handlers } = useLoginState()
  const { performLoginSuccessActions } = handlers
  const newDeviceSecret = uuidv4()
  const trustedDevices = useAppSelector(getTrustedDeviceDetails)

  const handleOutdatedBiometricsCredentials = () => {
    handlers.setErrorMessage(
      'Your PIN/Biometrics is invalid. Please login using your account number and password.'
    )
  }

  useBiometricsCredentials((accountNumber: number, password: string) => {
    const storedDeviceDetailsForAccount = trustedDevices[accountNumber.toString()]

    const loginData = {
      accountIdentifier: accountNumber.toString(),
      password,
      rememberAccount: true,
      keepLoggedIn: true,
      loginViaBioAuth: true,
      deviceSecret: storedDeviceDetailsForAccount?.deviceSecret ?? newDeviceSecret,
      deviceGroupKey: storedDeviceDetailsForAccount?.deviceGroupKey,
      deviceKey: storedDeviceDetailsForAccount?.deviceKey,
    }

    handlers.onLoginDataChange(loginData)
    handlers.setIsBusy(true)

    attemptLogin(loginData)
      .then(res => {
        if (res.status === 'SUCCESSFUL') {
          performLoginSuccessActions(accountNumber)
          initializeWithCountIfNotInitialized(accountNumber, 2)
          trackLoginSuccess(accountNumber.toString(), true, true, true)
          incrementQuickCounter('login-successful-with-biometrics')
        } else {
          if (res.status === 'PASSWORD_CHANGE_REQUIRED') {
            navigateToChangeTempPassword({ accountNumber: accountNumber.toString(), password })
          } else if (res.status === 'MFA_REQUIRED') {
            handlers.handleStartMfaVerification?.(res)
          } else {
            handleOutdatedBiometricsCredentials()
          }
        }
      })
      .finally(() => {
        handlers.setIsBusy(false)
      })
  })

  return (
    <LoginContainerStyled data-testid='login-overlay' ref={loginContainerRef}>
      <h1>
        <Icon type='person' size='1.8rem' /> {state.workflow}
        <button
          aria-label='Close button'
          type='button'
          onClick={handlers.close}
          disabled={state.isBusy}
        >
          <Icon type='cross' title='Close' />
        </button>
      </h1>

      {state.workflow === 'Login' ? (
        <div>
          <LoginMain state={state} handlers={handlers} />
        </div>
      ) : null}

      {state.workflow === 'Login Verification' ? (
        <LoginVerificationCodePage state={state} handlers={handlers} />
      ) : null}

      {state.workflow === 'Password Reset' ? (
        <div>
          <PasswordReset
            accountNumber={state.accountIdentifier}
            setWorkflowState={handlers.setWorkflow}
            handleSignUp={handlers.signUp}
          />
        </div>
      ) : null}

      {state.workflow === 'Account Number Recovery' ? (
        <div>
          <AccountNumberRecovery
            handleSignUp={handlers.signUp}
            handleReturnToLogin={() => handlers.setWorkflow('Login')}
          />
        </div>
      ) : null}
    </LoginContainerStyled>
  )
})

LoginOverlay.displayName = 'LoginOverlay'
